import React, { useMemo } from 'react';
import { round } from 'lodash';
import { Box, Card, Stack, Typography } from '@mui/material';

import TideArrow from 'components/Icons/TideArrow';
import type { RateSurfSample } from 'types/rateTheSurf';
import type { SurfHeight, Speed, Height, Units } from 'types/units';
import { roundSurfHeight } from 'utils/roundUnits';
import getUnitType from 'utils/getUnitType';
import formatWaveHeight from 'utils/formatWaveHeight';
import mapDegreesCardinal from 'utils/mapDegreesCardinal';
import conditionIsFlat from 'utils/conditionIsFlat';

import styles from './RateSurfConditions.module.scss';

interface RateSurfConditionsProps {
  slideData: RateSurfSample & {
    id: string;
    name: string;
  };
  slideIndex: number;
  units: Pick<Units, 'swellHeight' | 'tideHeight' | 'waveHeight' | 'windSpeed'>;
}

const formatDeg = (deg: number): string => `${Math.round(deg)}°`;

const formatSurfRange = (
  surf: { min: number; max: number; plus: boolean },
  units: SurfHeight,
): string => {
  const { min, max, plus } = surf;
  const unitType = getUnitType(units);
  const roundedMin = roundSurfHeight(min, unitType || units);
  const roundedMax = roundSurfHeight(max, unitType || units);
  const formattedUnit = formatWaveHeight(units, true);

  if (roundedMin === roundedMax) {
    return `${roundedMin}${plus ? '+' : ''}${formattedUnit}`;
  }
  return `${roundedMin}-${roundedMax}${plus ? '+' : ''}${formattedUnit}`;
};

const formatHeight = (height: number, units: Height): string => {
  const unitType = getUnitType(units);
  const rounded = roundSurfHeight(height, unitType || units);
  const formattedUnit = formatWaveHeight(units, true);
  return `${rounded}${formattedUnit}`;
};

const formatWindSpeed = (speed: number, units: Speed): string => {
  const rounded = round(speed * 10) / 10;
  const display = rounded % 1 === 0 ? round(rounded) : rounded;
  return `${display}${units.toLowerCase()}`;
};

const formatWindDirection = (degrees: number): string => {
  if (degrees === 0) return 'Variable';
  return mapDegreesCardinal(degrees) || 'N/A';
};

const RateSurfConditions: React.FC<RateSurfConditionsProps> = ({
  slideData,
  slideIndex,
  units,
}) => {
  const isFlat = useMemo(
    () => conditionIsFlat(slideData.surf.max, units.waveHeight as SurfHeight),
    [slideData.surf.max, units.waveHeight],
  );

  const formattedSurf = useMemo(() => {
    if (isFlat) return 'Flat';
    return formatSurfRange(slideData.surf, units.waveHeight as SurfHeight);
  }, [isFlat, slideData.surf, units.waveHeight]);

  const formattedWind = useMemo(
    () => ({
      speed: formatWindSpeed(slideData.wind.speed, units.windSpeed as Speed),
      direction: formatWindDirection(slideData.wind.direction),
    }),
    [slideData.wind.speed, slideData.wind.direction, units.windSpeed],
  );

  const formattedTide = useMemo(
    () => formatHeight(slideData.tide.height, units.tideHeight as Height),
    [slideData.tide.height, units.tideHeight],
  );

  const formattedSwells = useMemo(
    () =>
      slideData.swells.slice(0, 3).map((s) => ({
        height: formatHeight(s.height, units.swellHeight as Height),
        period: `${round(s.period)}s`,
        direction: formatDeg(s.direction),
      })),
    [slideData.swells, units.swellHeight],
  );

  return (
    <Card className={styles.card}>
      <Box className={styles.grid}>
        <Box>
          <Typography variant="caption" color="text.secondary" className={styles.label}>
            SURF
          </Typography>
          <Typography variant="footnote" className={styles.value} color="text.primary">
            {formattedSurf}
          </Typography>
        </Box>
        <Box className={styles.swellSection}>
          <Typography variant="caption" color="text.secondary" className={styles.label}>
            SWELL
          </Typography>
          <Stack spacing={0} alignItems="flex-start">
            {formattedSwells.map((swell, si) => (
              <Stack
                key={`swell-${slideIndex}-${si}`}
                direction="row"
                spacing={0}
                alignItems="center"
                className={styles.swellRow}
              >
                <Typography variant="footnote" className={styles.swellHeight}>
                  {swell.height}
                </Typography>
                <Typography variant="footnote" className={styles.swellPeriod}>
                  {swell.period}
                </Typography>
                <Typography variant="footnote" className={styles.swellDirection}>
                  {swell.direction}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary" className={styles.label}>
            WIND
          </Typography>
          <Typography variant="footnote" className={styles.value}>
            {formattedWind.speed} {formattedWind.direction}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary" className={styles.label}>
            TIDE
          </Typography>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Typography variant="footnote" className={styles.value}>
              {formattedTide}
            </Typography>
            {slideData.tide.trend && (
              <Box className={styles.tideArrowWrapper}>
                <TideArrow
                  direction={slideData.tide.trend === 'RISING' ? 'rising' : 'falling'}
                  height={16}
                  width={12}
                />
              </Box>
            )}
          </Stack>
        </Box>
      </Box>
    </Card>
  );
};

export default RateSurfConditions;
